import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { MessageCircle, Send, Target, Loader2, X, CalendarPlus } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';
import { useAuthStore } from '../../store/authStore';
import { useFeatureAccess } from '../ProFeatureBadge';
import { subscriptionService, ROOKIE_LIMITS } from '../../services/subscriptionService';
import PageHeader from '../layout/PageHeader';
import type { CreatedPracticeInfo } from './CoachStevePracticeForm';
import { CoachSteveGamePlanForm } from './CoachSteveGamePlanForm';
import { CoachSteveCreateGameForm } from './CoachSteveCreateGameForm';
import { generatePracticePlan } from '../../services/aiService.simplified';
import { PracticeService } from '../../services/database';
import type { PracticePlanRequest } from '../../services/aiService.types';
import { saveDrills } from '../../services/practice/practiceService';
import type { Drill } from '../../types';
import { generateAIContent } from '../../api/ai';
import { playerCardService } from '../../services/playerCardService';
import { buildTeamContextForAgent, buildGameStatsContextForAgent } from './coachSteveAgentContext';
import { supabase } from '../../lib/supabase';
import { getSportPositionGroups } from '../../utils/sportUtils';
import { getRoleTypesForSport } from '../../types/playerCard';
import { INJURY_STATUS } from '../../constants/injuries';
import type { InjuryStatus } from '../../constants/injuries';
import { InjuryService } from '../../services/injuryService';
import type { Game } from '../../services/gameService';
import { assignPlayerToFormation } from '../../services/depthChartAssignmentService';

export type MessageRole = 'user' | 'assistant';

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  createdAt: Date;
  /** After form submit, link to the created resource */
  link?: { label: string; href: string };
}

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content: `Hey, I'm Coach Steve. Ask me anything about practices, drills, or game planning — or say **create a practice plan**, **create a game plan**, or **create a game** when you're ready and I'll walk you through it.\n\nYou can **add a player** (e.g. "Add John Doe as a sub. He can play infield and outfield.") or **set injury status** (e.g. "Mark John Doe out", "set #5 as questionable"), or **add a game** to the schedule. If you don’t tell me the player’s position groups, I’ll ask.`,
  createdAt: new Date(),
};

/** True only when the user clearly asks to create a practice (not just mentions "practice"). */
function isCreatePracticeIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const createPatterns = [
    /^(create|make|generate|build)\s+(me\s+)?(a\s+)?practice(\s+plan)?/i,
    /^i\s+(want|need)\s+(to\s+create\s+)?(a\s+)?practice(\s+plan)?/i,
    /^set\s+up\s+(a\s+)?practice(\s+plan)?/i,
    /^(can you\s+)?(please\s+)?create\s+(a\s+)?practice(\s+plan)?/i,
    /^create\s+(a\s+)?practice(\s+plan)?\s/,
  ];
  return createPatterns.some((re) => re.test(lower));
}

/** True only when the user clearly asks to create a game plan (not a game/schedule). */
function isCreateGamePlanIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const createPatterns = [
    /^(create|make|generate|build)\s+(me\s+)?(a\s+)?game\s+plan/i,
    /^i\s+(want|need)\s+(to\s+create\s+)?(a\s+)?game\s+plan/i,
    /^(can you\s+)?(please\s+)?create\s+(a\s+)?game\s+plan/i,
    /^create\s+(a\s+)?game\s+plan\s/,
  ];
  return createPatterns.some((re) => re.test(lower));
}

/** True when the user asks to create/save a formation (depth chart). */
function isCreateFormationIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const patterns = [
    /\b(create|make|save|add)\s+(?:a\s+)?(?:new\s+)?formation\b/i,
    /\b(create|make|save)\s+(?:the|that|this)\s+formation\b/i,
    /^(?:yes\s+)?(?:please\s+)?create\s+(?:the|that|this)\s+formation/i,
    /\b(?:can you\s+)?(?:please\s+)?(create|save)\s+(?:the|that|this)\s+formation/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/** Common position name -> abbreviation for assign-player-to-formation parsing. */
const POSITION_ALIASES: Record<string, string> = {
  quarterback: 'QB', qb: 'QB', 'running back': 'RB', rb: 'RB', receiver: 'WR', wr: 'WR',
  'wide receiver': 'WR', 'tight end': 'TE', te: 'TE', fullback: 'FB', fb: 'FB',
  'left tackle': 'LT', lt: 'LT', 'right tackle': 'RT', rt: 'RT', center: 'C', c: 'C',
  'left guard': 'LG', lg: 'LG', 'right guard': 'RG', rg: 'RG', slot: 'SL', sl: 'SL',
  'slot receiver': 'SL', 'halfback': 'HB', hb: 'HB', wingback: 'WB', wb: 'WB',
  'defensive end': 'RDE', 'left defensive end': 'LDE', 'right defensive end': 'RDE',
  lde: 'LDE', rde: 'RDE', 'defensive tackle': 'LDT', ldt: 'LDT', rdt: 'RDT', nt: 'NT',
  'nose tackle': 'NT', 'linebacker': 'MLB', mlb: 'MLB', wlb: 'WLB', slb: 'SLB',
  'cornerback': 'LCB', lcb: 'LCB', rcb: 'RCB', 'safety': 'FS', fs: 'FS', ss: 'SS',
  'free safety': 'FS', 'strong safety': 'SS', 'nickelback': 'NB', nb: 'NB',
};

/** True when the user asks to assign or move a player to a position (with or without formation). */
function isAssignPlayerToFormationIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const patterns = [
    // With formation: "put X at Y in Spread", "assign #5 to RB in the Spread formation", "move Ace to LDT in 4-3"
    /\b(put|assign|set|move)\s+.+\s+(?:at|to|as)\s+.+\s+(?:in|for)\s+(?:the\s+)?.+\s*(?:formation)?\s*$/i,
    /\b(?:assign|put|move)\s+.+\s+(?:at|to)\s+.+\s+in\s+/i,
    // Without formation: "assign Ace to the LDT position", "put Ace at LDT position", "move Ace to LDT"
    /\b(assign|put|set|move)\s+.+\s+(?:to|at|as)\s+(?:the\s+)?.+\s*(?:position)?\s*$/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/** Normalize position phrase: strip "the " prefix and " position" suffix, then resolve alias. */
function normalizePositionKey(positionPart: string): string {
  const raw = positionPart.trim().replace(/^\s*the\s+/i, '').replace(/\s+position\s*$/i, '').trim();
  const posLower = raw.toLowerCase();
  return POSITION_ALIASES[posLower] || raw.toUpperCase();
}

/**
 * Parse "put John at QB in Spread" / "assign #5 to RB in Spread" / "move Ace to LDT in 4-3" style messages.
 * Returns player ref, position key, and formation name (formation required).
 */
function extractAssignFormationFromMessage(text: string): { playerRef: string; positionKey: string; formationName: string } | null {
  const trimmed = text.trim();
  const match = trimmed.match(/^(?:put|assign|set|move)\s+(.+?)\s+(?:at|to|as)\s+(.+?)\s+(?:in|for)\s+(?:the\s+)?(.+?)(?:\s+formation)?\s*$/i);
  if (!match) return null;
  const [, playerRef, positionPart, formationName] = match;
  if (!playerRef?.trim() || !positionPart?.trim() || !formationName?.trim()) return null;
  const positionKey = normalizePositionKey(positionPart);
  return {
    playerRef: playerRef.trim(),
    positionKey,
    formationName: formationName.trim(),
  };
}

/**
 * Parse "assign Ace to the LDT position" / "put Ace at LDT position" / "move Ace to LDT" (no formation).
 * Returns player ref and position key so we can ask for the formation.
 */
function extractAssignPlayerPositionOnly(text: string): { playerRef: string; positionKey: string } | null {
  const trimmed = text.trim();
  // "assign X to [the] Y [position]" or "put X at [the] Y [position]" or "move X to [the] Y [position]"
  const match = trimmed.match(/^(?:assign|put|set|move)\s+(.+?)\s+(?:to|at|as)\s+(?:the\s+)?(.+?)\s*$/i);
  if (!match) return null;
  const [, playerRef, positionPart] = match;
  if (!playerRef?.trim() || !positionPart?.trim()) return null;
  const positionKey = normalizePositionKey(positionPart);
  return { playerRef: playerRef.trim(), positionKey };
}

function playerRefToIdentifier(playerRef: string): PlayerIdentifier {
  const numMatch = playerRef.match(/^#?\s*(\d{1,3})\s*$/);
  if (numMatch) return { number: parseInt(numMatch[1], 10) };
  const parts = playerRef.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  if (parts.length === 1) return { name: parts[0] };
  return {};
}

/** True when the user asks to create/add/schedule a game (schedule entry), not a game plan. */
function isCreateGameIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  if (/\bgame\s+plan\b/.test(lower)) return false;
  const patterns = [
    /^(create|add|schedule)\s+(a\s+)?game\b/i,
    /^(can you\s+)?(please\s+)?(create|add|schedule)\s+(a\s+)?game\b/i,
    /^i\s+(want|need)\s+to\s+(create|add|schedule)\s+(a\s+)?game\b/i,
    /\bcreate\s+(a\s+)?game\s*[.?!]?\s*$/i,
    /\badd\s+(a\s+)?game\s*[.?!]?\s*$/i,
    /\bschedule\s+(a\s+)?game\b/i,
    // "add a game" anywhere (e.g. "i'd like to add a game to the schedule")
    /\badd\s+(a\s+)?game\b/i,
    /\b(?:i'd|i\s+would)\s+like\s+to\s+add\s+(a\s+)?game\b/i,
    /\b(?:i\s+want|i\s+need)\s+to\s+add\s+(a\s+)?game\b/i,
    /\bgame\s+to\s+(?:the\s+)?schedule\b/i,
    /\badd\s+(a\s+)?game\s+to\s+(?:the\s+)?schedule\b/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/** True when the user asks to add/create a player so we run the real add flow instead of the general AI. Excludes game, game plan, formation, practice. */
function isCreatePlayerIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  // Don't treat other "create" targets as adding a player
  if (/\badd\s+(a\s+)?game\b/.test(lower) || /\bgame\s+to\s+(?:the\s+)?schedule\b/.test(lower)) return false;
  if (/\bformation\b/.test(lower)) return false;
  if (/\bgame\s+plan\b/.test(lower)) return false;
  if (/\b(create|make|add)\s+(?:a\s+)?practice\b/.test(lower)) return false;
  if (/\b(create|make|add)\s+(?:a\s+)?game\b/.test(lower)) return false;
  const patterns = [
    /\badd\s+(?:a\s+)?player\b/i,
    /\bcreate\s+(?:a\s+)?player\b/i,
    /\bnew\s+player\b/i,
    /\badd\s+.+\s+to\s+(?:the\s+)?roster\b/i,
    /\bput\s+.+\s+on\s+(?:the\s+)?roster\b/i,
    /\b(?:i'd|i\s+would)\s+like\s+to\s+add\b/i,
    /\b(?:i\s+want\s+to|please)\s+add\b/i,
    /\b(?:can\s+you|could\s+you)\s+add\b/i,
    // "add John Doe" — two name-like words (excludes "add the formation" via formation check above)
    /\badd\s+[A-Za-z][A-Za-z'\-]+\s+[A-Za-z][A-Za-z'\-]+/i,
  ];
  return patterns.some((re) => re.test(lower));
}

function extractPlayerName(text: string): { firstName?: string; lastName?: string } {
  // Prefer "player FirstName LastName" then "add FirstName LastName" / "put FirstName LastName"
  const m =
    text.match(/\bplayer\s+([A-Za-z][A-Za-z'\-]+)\s+([A-Za-z][A-Za-z'\-]+)\b/i) ||
    text.match(/\badd\s+([A-Za-z][A-Za-z'\-]+)\s+([A-Za-z][A-Za-z'\-]+)(?:\s+as|\s+to|\s*[.,]|$)/i) ||
    text.match(/\bput\s+([A-Za-z][A-Za-z'\-]+)\s+([A-Za-z][A-Za-z'\-]+)\b/i);
  if (!m) return {};
  return { firstName: m[1], lastName: m[2] };
}

function extractJerseyNumber(text: string): number | undefined {
  const m = text.match(/(?:#|number\s+)(\d{1,3})\b/i);
  if (!m) return undefined;
  const n = parseInt(m[1], 10);
  if (Number.isNaN(n)) return undefined;
  return n;
}

function extractIsSub(text: string): boolean {
  const lower = text.toLowerCase();
  if (/\bnot\s+a\s+sub\b/.test(lower) || /\bno\s+sub\b/.test(lower)) return false;
  if (/\bsub\b/.test(lower) || /\bsubstitute\b/.test(lower) || /\bfill[-\s]?in\b/.test(lower)) return true;
  return false;
}

/** True when the user asks to set or update a player's injury status. Kept flexible for natural phrasing. */
function isSetInjuryIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const patterns = [
    /\bmark\s+.+\s+(?:as\s+)?(?:out|questionable|doubtful|healthy)\b/i,
    /\bset\s+.+\s+(?:as|to)\s+(?:out|questionable|doubtful|healthy)\b/i,
    /\b(?:out|questionable|doubtful|healthy)\s+for\s+.+/i,
    /\binjury\s+(?:status\s+)?(?:for|on)\s+.+/i,
    /\bset\s+injury\b/i,
    /\bupdate\s+injury\b/i,
    /\b(?:#?\d+|[A-Za-z][A-Za-z'\-\s]+)\s+is\s+(?:out|questionable|doubtful|healthy)\b/i,
    /\b(?:#?\d+|[A-Za-z][A-Za-z'\-\s]+)\s+out\b/i,
    // Natural phrasing: "Cole is sick", "Finn is injured", "X should be marked out"
    /\bis\s+(?:sick|injured|hurt|unavailable)\b/i,
    /\bshould\s+be\s+marked\s+(?:out|down)\b/i,
    /\b(?:sick|injured|hurt)\s+and\s+should\s+be\s+marked\s+out\b/i,
    // Availability phrasing
    /\bavailability\s+status\b/i,
    /\bset\s+availability\b/i,
    /\bmake\s+.+\s+available\b/i,
    /\bactivate\s+.+\b/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/** Extract injury status (H/Q/D/O) from message text. Accepts synonyms (sick, injured, hurt = out). */
function extractInjuryStatusFromText(text: string): InjuryStatus | undefined {
  const lower = text.toLowerCase();
  if (/\bout\b/.test(lower)) return INJURY_STATUS.OUT;
  if (/\bsick\b/.test(lower) || /\binjured\b/.test(lower) || /\bhurt\b/.test(lower) || /\bunavailable\b/.test(lower)) return INJURY_STATUS.OUT;
  if (/\bmarked\s+out\b/.test(lower) || /\bmark\s+out\b/.test(lower)) return INJURY_STATUS.OUT;
  if (/\bquestionable\b/.test(lower) || /\bq\b/.test(lower)) return INJURY_STATUS.QUESTIONABLE;
  if (/\bdoubtful\b/.test(lower) || /\bd\b/.test(lower)) return INJURY_STATUS.DOUBTFUL;
  if (/\bhealthy\b/.test(lower) || /\bh\b/.test(lower)) return INJURY_STATUS.HEALTHY;
  if (/\bactivate\b/.test(lower) || /\bavailable\b/.test(lower) || /\bcleared\b/.test(lower)) return INJURY_STATUS.HEALTHY;
  return undefined;
}

/** Player identifier: number, first+last, or a single name token (first name, last name, or nickname). */
interface PlayerIdentifier {
  firstName?: string;
  lastName?: string;
  number?: number;
  /** Single name token when user says e.g. "mark Mike out" or "activate Rocket" — matched against first_name, last_name, nickname. */
  name?: string;
}

/** Extract player identifier (name or number) from an injury-related message. Flexible for natural phrasing. */
function extractPlayerFromInjuryMessage(text: string): PlayerIdentifier {
  const number = extractJerseyNumber(text);
  if (number != null) return { number };

  const namePart = /([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?/;

  // "Cole is sick and should be marked out" / "Finn is injured" / "X is hurt" / "X is out"
  const isSickInjured = text.match(new RegExp(`\\b${namePart.source}\\s+is\\s+(?:sick|injured|hurt|unavailable|out)(?:\\s+and\\s+should\\s+be\\s+marked\\s+out)?`, 'i'));
  if (isSickInjured) return isSickInjured[2] ? { firstName: isSickInjured[1], lastName: isSickInjured[2] } : { name: isSickInjured[1] };

  // "Cole should be marked out" / "Finn should be marked out"
  const shouldBeMarked = text.match(new RegExp(`\\b${namePart.source}\\s+should\\s+be\\s+marked\\s+(?:out|down)`, 'i'));
  if (shouldBeMarked) return shouldBeMarked[2] ? { firstName: shouldBeMarked[1], lastName: shouldBeMarked[2] } : { name: shouldBeMarked[1] };

  // "mark John Doe out" / "mark John out" / "mark Cole out"
  const markMatch = text.match(/\bmark\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+(?:as\s+)?(?:out|questionable|doubtful|healthy)/i);
  if (markMatch) return markMatch[2] ? { firstName: markMatch[1], lastName: markMatch[2] } : { name: markMatch[1] };

  // "set John out" / "set John Doe as out"
  const setAsMatch = text.match(/\bset\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+(?:as|to)\s+(?:out|questionable|doubtful|healthy)/i);
  if (setAsMatch) return setAsMatch[2] ? { firstName: setAsMatch[1], lastName: setAsMatch[2] } : { name: setAsMatch[1] };
  const setDirectMatch = text.match(/\bset\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+(?:out|questionable|doubtful|healthy)\b/i);
  if (setDirectMatch) return setDirectMatch[2] ? { firstName: setDirectMatch[1], lastName: setDirectMatch[2] } : { name: setDirectMatch[1] };

  // "John is out" / "Smith is out" / "John is questionable"
  const isMatch = text.match(/\b([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?\s+is\s+(?:out|questionable|doubtful|healthy)/i);
  if (isMatch) return isMatch[2] ? { firstName: isMatch[1], lastName: isMatch[2] } : { name: isMatch[1] };

  // "activate Mike" / "make Jake available"
  const activateMatch = text.match(/\bactivate\s+([A-Za-z][A-Za-z'\-]+(?:\s+[A-Za-z][A-Za-z'\-]+)?)\s*$/i);
  if (activateMatch) {
    const parts = activateMatch[1].trim().split(/\s+/);
    return parts.length === 2 ? { firstName: parts[0], lastName: parts[1] } : { name: activateMatch[1].trim() };
  }
  const makeAvailableMatch = text.match(/\bmake\s+([A-Za-z][A-Za-z'\-]+(?:\s+[A-Za-z][A-Za-z'\-]+)?)\s+available/i);
  if (makeAvailableMatch) {
    const parts = makeAvailableMatch[1].trim().split(/\s+/);
    return parts.length === 2 ? { firstName: parts[0], lastName: parts[1] } : { name: makeAvailableMatch[1].trim() };
  }

  // "injury for John" / "status for Smith"
  const injuryForMatch = text.match(/(?:injury|status)\s+(?:for|on)\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?/i);
  if (injuryForMatch) return injuryForMatch[2] ? { firstName: injuryForMatch[1], lastName: injuryForMatch[2] } : { name: injuryForMatch[1] };
  const statusForMatch = text.match(/(?:out|questionable|doubtful|healthy)\s+for\s+([A-Za-z][A-Za-z'\-]+)(?:\s+([A-Za-z][A-Za-z'\-]+))?/i);
  if (statusForMatch) return statusForMatch[2] ? { firstName: statusForMatch[1], lastName: statusForMatch[2] } : { name: statusForMatch[1] };

  return {};
}

function extractPositionGroupsFromText(text: string, sport: string): string[] {
  const lower = text.toLowerCase();
  const allowed = getSportPositionGroups(sport) || [];
  const picked = new Set<string>();

  if (sport === 'Baseball' || sport === 'Softball') {
    if (/\binfield\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('infield'));
      if (g) picked.add(g);
    }
    if (/\boutfield\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('outfield'));
      if (g) picked.add(g);
    }
    if (/\bpitch(er|ing|ers)\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('pitch'));
      if (g) picked.add(g);
    }
    if (/\bcatch(er|ing|ers)\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('catch'));
      if (g) picked.add(g);
    }
    if (/\b1st\b|\bfirst\s+base\b|\b1b\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('1st'));
      if (g) picked.add(g);
    }
  }

  for (const group of allowed) {
    const gLower = group.toLowerCase();
    const token = gLower.replace(/[^a-z0-9]+/g, ' ').trim();
    if (!token) continue;
    if (lower.includes(gLower) || lower.includes(token)) picked.add(group);
  }

  if (sport === 'Football' || sport === 'FlagFootball') {
    for (const group of allowed) {
      const re = new RegExp(`\\b${group.toLowerCase()}\\b`, 'i');
      if (re.test(lower)) picked.add(group);
    }
  }

  return Array.from(picked);
}

function inferRoleTypesFromGroups(sport: string, groups: string[]): string[] {
  const allowed = getRoleTypesForSport(sport) || [];
  if (allowed.length === 0) return [];

  const lowerGroups = groups.map((g) => g.toLowerCase());

  if (sport === 'Baseball' || sport === 'Softball') {
    const roles = new Set<string>();
    if (lowerGroups.some((g) => g.includes('pitch') || g.includes('catch'))) roles.add('battery');
    if (lowerGroups.some((g) => g.includes('infield') || g.includes('1st'))) roles.add('infield');
    if (lowerGroups.some((g) => g.includes('outfield'))) roles.add('outfield');
    const out = Array.from(roles).filter((r) => allowed.includes(r));
    return out.length ? out : allowed;
  }

  if (sport === 'Football' || sport === 'FlagFootball') {
    const roles = new Set<string>();
    if (groups.some((g) => ['OL', 'DL', 'EDGE'].includes(g))) roles.add('line');
    if (groups.some((g) => ['QB', 'WR', 'RB', 'TE', 'LB', 'DB', 'DL', 'EDGE', 'OL'].includes(g))) roles.add('skill');
    const out = Array.from(roles).filter((r) => allowed.includes(r));
    return out.length ? out : allowed;
  }

  const roles = new Set<string>();
  for (const g of lowerGroups) {
    for (const role of allowed) {
      const r = role.toLowerCase();
      if (g.includes(r) || g.includes(`${r}s`)) roles.add(role);
    }
  }
  const out = Array.from(roles);
  return out.length ? out : allowed;
}

function getTomorrowDateStr(): string {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

interface RosterPlayerForInjury {
  id: string;
  first_name: string;
  last_name: string;
  number?: number;
  nickname?: string | null;
}

async function fetchRosterForInjury(teamId: string): Promise<RosterPlayerForInjury[]> {
  const { data: playersData, error: playersError } = await supabase
    .from('players')
    .select('id, first_name, last_name, number')
    .eq('team_id', teamId);
  if (playersError || !playersData) return [];

  const { data: cardsData } = await supabase
    .from('player_cards')
    .select('player_id, nickname')
    .eq('team_id', teamId);
  const nicknameByPlayerId = new Map<string, string | null>();
  (cardsData || []).forEach((row: { player_id: string; nickname: string | null }) => {
    nicknameByPlayerId.set(row.player_id, row.nickname ?? null);
  });

  return (playersData as RosterPlayerForInjury[]).map((p) => ({
    ...p,
    nickname: nicknameByPlayerId.get(p.id) ?? undefined,
  }));
}

function findPlayersForInjury(
  roster: RosterPlayerForInjury[],
  identifier: PlayerIdentifier
): RosterPlayerForInjury[] {
  if (identifier.number != null) {
    return roster.filter((p) => p.number === identifier.number);
  }
  const first = (identifier.firstName || '').trim().toLowerCase();
  const last = (identifier.lastName || '').trim().toLowerCase();
  const singleName = (identifier.name || '').trim().toLowerCase();

  // Full first + last: match both
  if (first && last) {
    return roster.filter(
      (p) =>
        p.first_name.toLowerCase() === first &&
        p.last_name.toLowerCase() === last
    );
  }

  // Single token: match against first name, last name, or nickname (how coaches usually refer to players)
  const token = first || last || singleName;
  if (token) {
    return roster.filter((p) => {
      const pFirst = p.first_name.toLowerCase();
      const pLast = p.last_name.toLowerCase();
      const pNick = (p.nickname || '').toLowerCase();
      return pFirst === token || pLast === token || pNick === token;
    });
  }

  return [];
}

/**
 * Parse natural language game details from a message (e.g. "this coming saturday against the Bolingbrook Rampage at 1pm").
 * Returns values suitable for pre-filling the create-game form.
 */
function parseGameDetailsFromMessage(text: string): {
  date?: string;
  opponent_name?: string;
  game_time?: string;
} {
  const result: { date?: string; opponent_name?: string; game_time?: string } = {};
  const lower = text.toLowerCase().trim();

  // Opponent: "against the Bolingbrook Rampage", "vs Bolingbrook Rampage"
  const againstMatch = text.match(/(?:against|vs\.?)\s+(?:the\s+)?([A-Za-z0-9\s\-']+?)(?=\s+at\s+\d|\s+on\s+|\s*$|,|\.)/i);
  if (againstMatch && againstMatch[1].trim()) {
    result.opponent_name = againstMatch[1].trim();
  }

  // Time: "at 1pm", "1pm", "at 10:30 am", "10:30am"
  const timeMatch = lower.match(/(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/);
  if (timeMatch) {
    let h = parseInt(timeMatch[1], 10);
    const m = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
    const ampm = timeMatch[3];
    if (ampm === 'pm' && h < 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    result.game_time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // Date: "this coming saturday", "next saturday", "saturday", "this saturday"
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayIndex = dayNames.findIndex((d) => lower.includes(d));
  if (dayIndex !== -1) {
    const today = new Date();
    const todayDay = today.getDay();
    let daysAhead = dayIndex - todayDay;
    if (daysAhead <= 0 || /\bnext\s+/.test(lower)) daysAhead += 7;
    const target = new Date(today);
    target.setDate(today.getDate() + daysAhead);
    result.date = target.toISOString().split('T')[0];
  }

  return result;
}

/**
 * Extract focus/topics from a practice request so we don't ask again.
 * e.g. "create a practice focused on tackling" -> "tackling"
 *      "practice for base running and bunting" -> "base running and bunting"
 */
function extractPracticeFocusFromMessage(text: string): string | null {
  const lower = text.toLowerCase().trim();
  const patterns = [
    /(?:focus(?:ed|ing)?\s+on|about|around)\s+(.+)/i,
    /(?:working\s+on|work\s+on)\s+(.+)/i,
    /(?:practice|plan)\s+(?:for|on)\s+(.+)/i,
    /(?:want|need)\s+(?:a\s+)?practice\s+(?:for|on|about)?\s*(.+)/i,
    /create\s+(?:a\s+)?practice\s+(?:plan\s+)?(?:for|on|about)?\s*(.+)/i,
    /(?:for|on)\s+(.+?)(?:\s+practice|$)/i,
  ];
  for (const re of patterns) {
    const m = lower.match(re);
    if (m && m[1]) {
      const focus = m[1].replace(/\s+practice\s*$/i, '').trim();
      if (focus.length >= 2 && !/^(plan|it|one|that|this)$/.test(focus)) return focus;
    }
  }
  // If the message is more than a short request, treat the rest as focus (e.g. "create a practice - tackling and red zone")
  const createMatch = lower.match(/create\s+(?:a\s+)?practice\s*(?:plan)?\s*[\.\-—,:]\s*(.+)/);
  if (createMatch && createMatch[1].trim().length >= 2) return createMatch[1].trim();
  const practiceMatch = lower.match(/practice\s*(?:plan)?\s*[\.\-—,:]\s*(.+)/);
  if (practiceMatch && practiceMatch[1].trim().length >= 2) return practiceMatch[1].trim();
  return null;
}

export interface CoachSteveProps {
  /** When true, render as a side panel (no page header, compact header with close) */
  isPanel?: boolean;
  /** Called when user closes the panel */
  onClose?: () => void;
}

export const CoachSteve: React.FC<CoachSteveProps> = ({ isPanel = false, onClose }) => {
  const { user } = useAuthStore();
  const { currentTeam } = useTeamStore();
  const { checkFeatureAccess } = useFeatureAccess();
  const [messages, setMessages] = useState<ChatMessage[]>(() => [WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [pendingAction, setPendingAction] = useState<'practice' | 'game_plan' | 'create_game' | null>(null);
  const [createGameInitialValues, setCreateGameInitialValues] = useState<{
    date?: string;
    opponent_name?: string;
    game_time?: string;
  } | null>(null);
  const [pendingPracticeForFocus, setPendingPracticeForFocus] = useState<CreatedPracticeInfo | null>(null);
  const [pendingPlayerCreate, setPendingPlayerCreate] = useState<{
    firstName?: string;
    lastName?: string;
    number?: number;
    isSub?: boolean;
  } | null>(null);
  const [pendingInjuryUpdate, setPendingInjuryUpdate] = useState<
    | { kind: 'need_player'; status: InjuryStatus }
    | { kind: 'need_status'; playerId: string; playerName: string }
    | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string | null>(null);
  const [teamContextForAgent, setTeamContextForAgent] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messageIdCounterRef = useRef(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingAction, generationProgress]);

  // Load live team roster + attributes for agent context (so Coach Steve can use app data for lineups, positions, etc.)
  useEffect(() => {
    if (!currentTeam?.id) {
      setTeamContextForAgent(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await refreshTeamContextForAgent();
      } catch (e) {
        if (!cancelled) setTeamContextForAgent(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentTeam?.id]);

  const addMessage = (role: MessageRole, content: string, link?: { label: string; href: string }) => {
    const id = `msg-${Date.now()}-${++messageIdCounterRef.current}`;
    setMessages((prev) => [
      ...prev,
      {
        id,
        role,
        content,
        createdAt: new Date(),
        link,
      },
    ]);
  };

  const refreshTeamContextForAgent = async () => {
    if (!currentTeam?.id) {
      setTeamContextForAgent(null);
      return;
    }
    try {
      const cards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
      if (cards.length > 0) {
        let context = buildTeamContextForAgent(currentTeam, { cardsWithPlayers: cards });
        const gameStatsBlock = await buildGameStatsContextForAgent(currentTeam.id, currentTeam.sport, cards);
        if (gameStatsBlock) context = context + '\n\n' + gameStatsBlock;
        setTeamContextForAgent(context);
        return;
      }
      const { data: players } = await supabase
        .from('players')
        .select('id, first_name, last_name, number, position, height, weight, speed, strength, experience, injury_status, batting_hand, batting_average, on_base_percentage')
        .eq('team_id', currentTeam.id)
        .order('last_name');
      const context = buildTeamContextForAgent(currentTeam, { playersOnly: players ?? [] });
      setTeamContextForAgent(context);
    } catch {
      setTeamContextForAgent(null);
    }
  };

  const createPlaceholderPractice = async (): Promise<CreatedPracticeInfo | null> => {
    if (!currentTeam) return null;
    const date = getTomorrowDateStr();
    const startTime = currentTeam.defaultPracticeStartTime || '17:00';
    const endTime = currentTeam.defaultPracticeEndTime || '18:30';
    const location = currentTeam.defaultPracticeLocation || 'Field';
    const theme = 'Team Day';
    const nowIso = new Date().toISOString();
    const practiceData = {
      team_id: currentTeam.id,
      title: `Practice - ${theme}`,
      date,
      start_time: startTime,
      end_time: endTime,
      location,
      // Legacy field still used by some parts of the app
      theme,
      focus_areas: [],
      drills: [],
      created_at: nowIso,
      updated_at: nowIso,
    };
    const newPractice = await PracticeService.createPractice(practiceData);
    if (!newPractice) return null;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    let durationMinutes = endH * 60 + endM - (startH * 60 + startM);
    if (durationMinutes <= 0) durationMinutes += 24 * 60;
    return {
      practiceId: newPractice.id,
      date,
      startTime,
      endTime,
      location,
      theme,
      team: currentTeam,
      durationMinutes,
    };
  };

  const runPlanGeneration = async (
    info: CreatedPracticeInfo,
    focusText: string,
    onProgressUpdate: (message: string) => void
  ) => {
    const aiRequest: PracticePlanRequest = {
      team: info.team,
      duration: info.durationMinutes,
      theme: info.theme as any,
      focusAreas: [focusText],
      date: info.date,
      startTime: info.startTime,
      location: info.location,
      ageLevel: info.team.ageLevel,
      experienceLevel: info.team.experienceLevel,
    };
    let totalDrills = 0;
    let completedCount = 0;
    const plan = await generatePracticePlan(aiRequest, (progress) => {
      if (progress.status === 'count-update' && progress.totalDrills) {
        totalDrills = progress.totalDrills;
        onProgressUpdate(`Preparing your practice plan (${totalDrills} drills)…`);
      } else if (progress.status === 'generating' && progress.drillName) {
        const n = totalDrills ? completedCount + 1 : 0;
        const suffix = totalDrills ? ` (${n}/${totalDrills})` : '';
        onProgressUpdate(`Preparing: **${progress.drillName}**${suffix}`);
      } else if (progress.status === 'completed' && progress.drillName) {
        completedCount += 1;
        const suffix = totalDrills ? ` (${completedCount}/${totalDrills})` : '';
        onProgressUpdate(`Prepared: ${progress.drillName}${suffix}`);
      }
    });

    const collectedDrills: Omit<Drill, 'id'>[] = (plan.drills || []).map((d: any) => ({
      name: d.name,
      duration: d.duration,
      description: d.description || '',
      focus_areas: d.focus_area ? [d.focus_area] : (d.focus_areas || ['Team']),
      intensity_level: d.intensity_level,
      position_groups: (d.position_groups || []).map((pg: any) => ({
        ...(typeof pg === 'string' ? { group_name: pg, steps: [] } : pg),
        steps: (typeof pg === 'object' && pg.steps ? pg.steps : []).map((s: any) => ({
          ...s,
          coaching_points: s.coachingPoints || s.coaching_points || [],
        })),
      })),
      practice_id: info.practiceId,
    })) as any;

    if (collectedDrills.length > 0) {
      await saveDrills(info.practiceId, collectedDrills as Drill[]);
    }
    return plan;
  };

  const startPracticePlaceholderFlow = async (userMessage?: string) => {
    const extractedFocus = userMessage ? extractPracticeFocusFromMessage(userMessage) : null;
    addMessage('assistant', extractedFocus
      ? `Sure, I'll create a practice and build the plan around **${extractedFocus}**.`
      : 'Sure, let me create a placeholder for your practice.');
    setIsLoading(true);
    try {
      const info = await createPlaceholderPractice();
      if (!info) {
        addMessage('assistant', "I wasn't able to create the practice. Try again or set one up from the Practices page.");
        setIsLoading(false);
        return;
      }
      toast.success('Placeholder practice created.');

      if (extractedFocus) {
        setGenerationProgress('Preparing your practice plan…');
        try {
          await runPlanGeneration(info, extractedFocus, setGenerationProgress);
          setGenerationProgress(null);
          toast.success('Practice plan generated.');
          addMessage(
            'assistant',
            `I've built your practice around **${extractedFocus}**. Here’s your practice.`,
            { label: `Practice ${info.date}`, href: '/practices' }
          );
        } catch (err) {
          setGenerationProgress(null);
          console.error(err);
          toast.error(err instanceof Error ? err.message : 'Failed to generate practice plan');
          addMessage('assistant', 'Something went wrong generating the plan. I’ve still got your placeholder — tell me what you’d like to focus on and I’ll try again.');
          handlePracticeCreated(info);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      handlePracticeCreated(info);
    } catch (err) {
      console.error(err);
      addMessage('assistant', "Something went wrong creating the placeholder. You can create a practice from the Practices page.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = async (action: 'practice' | 'game_plan') => {
    if (!currentTeam) {
      addMessage('assistant', "Please select a team first from the dashboard so I know which team we're planning for.");
      return;
    }
    if (action === 'game_plan' && currentTeam.sport !== 'Football') {
      addMessage('assistant', "Game plans are only available for Football. I can help you create a **practice plan** instead.");
      return;
    }
    if (action === 'practice' && !checkFeatureAccess('AI-powered practice planning')) {
      addMessage('assistant', "AI-powered practice planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more.");
      return;
    }
    if (action === 'game_plan' && !checkFeatureAccess('AI-powered game planning')) {
      addMessage('assistant', "AI-powered game planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more.");
      return;
    }
    if (action === 'practice') {
      addMessage('user', 'Create a practice plan');
      await startPracticePlaceholderFlow();
      return;
    }
    addMessage('user', 'Create a game plan');
    setPendingAction(action);
  };

  const handlePracticeCreated = (info: CreatedPracticeInfo) => {
    setPendingAction(null);
    const timeRange = `${info.startTime}–${info.endTime}`;
    addMessage(
      'assistant',
      `I've got a placeholder set up for **${info.date}** from ${timeRange} at **${info.location}** (theme: **${info.theme}**). What would you like to focus on for this practice — any specific drills or areas?`
    );
    setPendingPracticeForFocus(info);
  };

  const handleFormSuccess = (
    action: 'practice' | 'game_plan',
    result: { id: string; label: string; href: string }
  ) => {
    setPendingAction(null);
    addMessage('assistant', `Done. I've created your ${action === 'practice' ? 'practice' : 'game plan'}. You can open it below.`, {
      label: result.label,
      href: result.href,
    });
  };

  const handleFormCancel = () => {
    setPendingAction(null);
    addMessage('assistant', 'No problem. Tell me what you’d like to do next, or use the shortcuts above.');
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput('');
    addMessage('user', text);

    // Pending "add player" follow-up: user is providing missing position groups (or name).
    if (pendingPlayerCreate) {
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard.");
        setPendingPlayerCreate(null);
        return;
      }
      setIsLoading(true);
      try {
        const sport = currentTeam.sport;
        const pending = pendingPlayerCreate;
        const nameFromReply = extractPlayerName(text);
        const firstName = pending.firstName || nameFromReply.firstName;
        const lastName = pending.lastName || nameFromReply.lastName;
        const groups = extractPositionGroupsFromText(text, sport);

        if (!firstName || !lastName) {
          addMessage('assistant', "What's the player's first and last name?");
          setPendingPlayerCreate({ ...pending, firstName, lastName });
          return;
        }

        if (groups.length === 0) {
          const options = getSportPositionGroups(sport);
          addMessage(
            'assistant',
            `Which position group(s) should I set for **${firstName} ${lastName}**?\nOptions: ${options.length ? options.join(', ') : 'none available for this sport'}.`
          );
          setPendingPlayerCreate({ ...pending, firstName, lastName });
          return;
        }

        const number = pending.number;
        const isSub = pending.isSub ?? false;
        const roleTypes = inferRoleTypesFromGroups(sport, groups);

        const { data: newPlayer, error: insertError } = await supabase
          .from('players')
          .insert({
            team_id: currentTeam.id,
            first_name: firstName,
            last_name: lastName,
            number: number ?? null,
            position: groups[0] || '--',
            experience: 'Beginner',
            injury_status: 'H',
            is_sub: isSub,
          })
          .select('id')
          .single();

        if (insertError || !newPlayer?.id) {
          console.error('Error creating player:', insertError);
          addMessage('assistant', "I couldn't create that player. Try again, or add them from the Roster page.");
          return;
        }

        const card = await playerCardService.getOrCreatePlayerCard(newPlayer.id, currentTeam.id);
        await playerCardService.updatePlayerCard(card.id, {
          eligible_positions: groups,
          role_types: roleTypes,
        });

        await refreshTeamContextForAgent();
        setPendingPlayerCreate(null);
        addMessage(
          'assistant',
          `Done — I added **${firstName} ${lastName}** to the roster${isSub ? ' as a **sub**' : ''}.\nPosition groups: ${groups.join(', ')}.`,
          { label: `Open ${firstName} ${lastName}`, href: `/player-cards/${newPlayer.id}` }
        );
      } catch (err) {
        console.error(err);
        addMessage('assistant', "Something went wrong adding that player. Try again, or add them from the Roster page.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (pendingPracticeForFocus) {
      const info = pendingPracticeForFocus;
      setPendingPracticeForFocus(null);
      setIsLoading(true);
      setGenerationProgress('Preparing your practice plan…');
      try {
        await runPlanGeneration(info, text, setGenerationProgress);
        setGenerationProgress(null);
        toast.success('Practice plan generated.');
        addMessage(
          'assistant',
          `I’ve built your practice plan around what you said. Here’s your practice.`,
          { label: `Practice ${info.date}`, href: '/practices' }
        );
      } catch (err) {
        setGenerationProgress(null);
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to generate practice plan');
        addMessage('assistant', 'Something went wrong generating the plan. You can try again from the Practices page, or tell me another focus and I’ll try again.');
        setPendingPracticeForFocus(info);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    // Intent order matters: check specific creation targets first (practice, game plan, game, formation)
    // so "create the formation" is never mis-routed to add player or general AI.
    if (isCreatePracticeIntent(text)) {
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard.");
        setIsLoading(false);
        return;
      }
      if (!checkFeatureAccess('AI-powered practice planning')) {
        addMessage('assistant', "AI-powered practice planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more.");
        setIsLoading(false);
        return;
      }
      await startPracticePlaceholderFlow(text);
      setIsLoading(false);
      return;
    }
    if (isCreateGamePlanIntent(text)) {
      setIsLoading(false);
      if (currentTeam?.sport === 'Football') handleQuickAction('game_plan');
      else addMessage('assistant', "Game plans are only for Football. I can help with a **practice plan** — just ask to create one.");
      return;
    }
    if (isCreateGameIntent(text)) {
      setIsLoading(false);
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard so I can add a game to their schedule.");
        return;
      }
      addMessage('user', text.trim());
      const parsed = parseGameDetailsFromMessage(text);
      const hasDetails = parsed.opponent_name || parsed.date || parsed.game_time;
      addMessage(
        'assistant',
        hasDetails
          ? "Got it — I've filled in what you said. Confirm or edit below, then create the game."
          : "Sure — fill in the details below to add a game to the schedule."
      );
      setCreateGameInitialValues(Object.keys(parsed).length ? parsed : null);
      setPendingAction('create_game');
      return;
    }

    // Create formation: direct to Depth Chart (formations are created there, not via chat)
    if (isCreateFormationIntent(text)) {
      setIsLoading(false);
      addMessage(
        'assistant',
        "Formations are created in the **Depth Chart**. Go to **Depth Chart** in the menu, pick a unit (e.g. Offense or Defense), set up your positions and players the way you want, then use the **Formations** button to save it as a new formation. I can’t create formations from here — but I can help you add a **player**, create a **practice plan**, **game plan**, or **game** if you’d like."
      );
      return;
    }

    // Assign player to formation (e.g. "put John at QB in Spread", "assign Ace to LDT in 4-3", or "assign Ace to the LDT position")
    if (isAssignPlayerToFormationIntent(text)) {
      setIsLoading(false);
      if (!currentTeam) {
        addMessage('assistant', 'Please select a team first from the dashboard.');
        return;
      }
      const sport = currentTeam.sport;
      if (sport !== 'Football' && sport !== 'FlagFootball') {
        addMessage('assistant', "Assigning players to formations is only for **Football** and **Flag Football**. Your team is **" + sport + "**.");
        return;
      }
      const parsedWithFormation = extractAssignFormationFromMessage(text);
      if (!parsedWithFormation) {
        const parsedPositionOnly = extractAssignPlayerPositionOnly(text);
        if (parsedPositionOnly) {
          addMessage(
            'assistant',
            `I can assign **${parsedPositionOnly.playerRef}** to **${parsedPositionOnly.positionKey}**. Tell me which formation — e.g. **assign ${parsedPositionOnly.playerRef} to ${parsedPositionOnly.positionKey} in Spread** (offense) or **in 4-3** (defense).`
          );
          return;
        }
        addMessage(
          'assistant',
          "I couldn't parse that. Use: **put [player] at [position] in [formation]** — e.g. \"put John at QB in Spread\" or \"assign #5 to RB in the Spread formation\"."
        );
        return;
      }
      setIsLoading(true);
      try {
        const roster = await fetchRosterForInjury(currentTeam.id);
        const identifier = playerRefToIdentifier(parsedWithFormation.playerRef);
        const matches = findPlayersForInjury(roster, identifier);
        if (matches.length === 0) {
          addMessage('assistant', "I don't see that player on the roster. Use their name or jersey number (e.g. \"put John at QB in Spread\" or \"assign #5 to RB in Spread\").");
          setIsLoading(false);
          return;
        }
        if (matches.length > 1) {
          const list = matches.map((p) => `**${p.first_name} ${p.last_name}**${p.number != null ? ` #${p.number}` : ''}`).join(', ');
          addMessage('assistant', `I found more than one: ${list}. Which one? (Use jersey number, e.g. assign #5 to RB in Spread.)`);
          setIsLoading(false);
          return;
        }
        const player = matches[0];
        const result = await assignPlayerToFormation(
          currentTeam.id,
          sport,
          parsedWithFormation.formationName,
          parsedWithFormation.positionKey,
          player.id,
          1
        );
        if (!result.success) {
          addMessage('assistant', result.error || "Couldn't update the depth chart. Check that the **formation** and **position** exist (e.g. Spread has QB, RB; 4-3 has LDE, LDT, MLB). You can set formations in **Depth Chart**.");
          setIsLoading(false);
          return;
        }
        await refreshTeamContextForAgent();
        const name = `${player.first_name} ${player.last_name}`.trim();
        const num = player.number != null ? ` #${player.number}` : '';
        if (result.previousPosition) {
          addMessage(
            'assistant',
            `Moved **${name}**${num} from **${result.previousPosition}** to **${parsedWithFormation.positionKey}** in the **${parsedWithFormation.formationName}** formation. A player can only be in one position per formation — this has been updated.`,
            { label: 'Open Depth Chart', href: '/depth-chart' }
          );
        } else {
          addMessage(
            'assistant',
            `Done — **${name}**${num} is now at **${parsedWithFormation.positionKey}** in the **${parsedWithFormation.formationName}** formation.`,
            { label: 'Open Depth Chart', href: '/depth-chart' }
          );
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('depth-chart-updated', { detail: { teamId: currentTeam.id } }));
        }
      } catch (err) {
        console.error(err);
        addMessage('assistant', "Something went wrong updating the depth chart. Try **Depth Chart** in the app to assign players manually.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Set injury status flow
    if (pendingInjuryUpdate || isSetInjuryIntent(text)) {
      if (!currentTeam) {
        addMessage('assistant', 'Please select a team first from the dashboard.');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        let status: InjuryStatus | undefined;
        let playerIdentifier: PlayerIdentifier = {};

        if (pendingInjuryUpdate) {
          if (pendingInjuryUpdate.kind === 'need_player') {
            status = pendingInjuryUpdate.status;
            playerIdentifier = extractPlayerFromInjuryMessage(text);
            const hasId =
              playerIdentifier.firstName ||
              playerIdentifier.lastName ||
              playerIdentifier.name ||
              playerIdentifier.number != null;
            if (!hasId) {
              addMessage('assistant', "Which player? Tell me their first name, last name, nickname, or jersey number (e.g. \"Mike\" or \"#5\").");
              setIsLoading(false);
              return;
            }
          } else {
            const statusFromText = extractInjuryStatusFromText(text);
            if (!statusFromText) {
              addMessage('assistant', `What injury status for **${pendingInjuryUpdate.playerName}**? Say **healthy**, **questionable**, **doubtful**, or **out**.`);
              setIsLoading(false);
              return;
            }
            await InjuryService.updatePlayerInjuryStatus(pendingInjuryUpdate.playerId, statusFromText, currentTeam.id);
            await refreshTeamContextForAgent();
            setPendingInjuryUpdate(null);
            const label = statusFromText === 'H' ? 'Healthy' : statusFromText === 'Q' ? 'Questionable' : statusFromText === 'D' ? 'Doubtful' : 'Out';
            addMessage('assistant', `Done — I set **${pendingInjuryUpdate.playerName}** as **${label}**.`);
            setIsLoading(false);
            return;
          }
        } else {
          status = extractInjuryStatusFromText(text);
          playerIdentifier = extractPlayerFromInjuryMessage(text);
        }

        if (!status) {
          const hasPlayerIdentifier =
            playerIdentifier.firstName ||
            playerIdentifier.lastName ||
            playerIdentifier.name ||
            playerIdentifier.number != null;
          if (hasPlayerIdentifier) {
            const roster = await fetchRosterForInjury(currentTeam.id);
            const matches = findPlayersForInjury(roster, playerIdentifier);
            if (matches.length === 1) {
              setPendingInjuryUpdate({
                kind: 'need_status',
                playerId: matches[0].id,
                playerName: `${matches[0].first_name} ${matches[0].last_name}`.trim(),
              });
              addMessage('assistant', `What injury status for **${matches[0].first_name} ${matches[0].last_name}**? Say **healthy**, **questionable**, **doubtful**, or **out**.`);
            } else if (matches.length > 1) {
              const list = matches.map((p) => `**${p.first_name} ${p.last_name}**${p.number != null ? ` #${p.number}` : ''}`).join(', ');
              addMessage('assistant', `I found more than one: ${list}. Which one did you mean? (You can say their number or full name, then the status — e.g. "mark #5 out".)`);
            } else {
              addMessage('assistant', "I don't see that player on the roster. You can use first name, last name, nickname, or jersey number (e.g. \"mark Mike out\" or \"set #5 as questionable\").");
            }
          } else {
            addMessage('assistant', "What status should I set? Say **healthy**, **questionable**, **doubtful**, or **out**. You can also say both: e.g. \"mark John Doe out\".");
          }
          setIsLoading(false);
          return;
        }

        const roster = await fetchRosterForInjury(currentTeam.id);
        const matches = findPlayersForInjury(roster, playerIdentifier);

        if (matches.length === 0) {
          addMessage('assistant', "I don't see that player on the roster. You can use first name, last name, nickname, or jersey number (e.g. \"mark Mike out\" or \"set #5 as questionable\").");
          setPendingInjuryUpdate(null);
          setIsLoading(false);
          return;
        }
        if (matches.length > 1) {
          const list = matches.map((p) => `**${p.first_name} ${p.last_name}**${p.number != null ? ` #${p.number}` : ''}`).join(', ');
          addMessage('assistant', `I found more than one: ${list}. Which one did you mean? (Say their number or full name, then the status — e.g. "mark #5 out".)`);
          setPendingInjuryUpdate(null);
          setIsLoading(false);
          return;
        }

        const player = matches[0];
        await InjuryService.updatePlayerInjuryStatus(player.id, status, currentTeam.id);
        await refreshTeamContextForAgent();
        setPendingInjuryUpdate(null);
        const label = status === 'H' ? 'Healthy' : status === 'Q' ? 'Questionable' : status === 'D' ? 'Doubtful' : 'Out';
        addMessage('assistant', `Done — I set **${player.first_name} ${player.last_name}**${player.number != null ? ` #${player.number}` : ''} as **${label}**.`);
      } catch (err) {
        console.error(err);
        addMessage('assistant', "Something went wrong updating injury status. Try again or set it from the Roster / Player Card.");
        setPendingInjuryUpdate(null);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Add player flow (creates a player + player card)
    if (isCreatePlayerIntent(text)) {
      if (!currentTeam) {
        addMessage('assistant', "Please select a team first from the dashboard.");
        setIsLoading(false);
        return;
      }
      const sport = currentTeam.sport;
      const { firstName, lastName } = extractPlayerName(text);
      const number = extractJerseyNumber(text);
      const isSub = extractIsSub(text);
      const groups = extractPositionGroupsFromText(text, sport);

      if (!firstName || !lastName) {
        addMessage('assistant', "Who should I add? Tell me the player's first and last name (and optionally their position groups).");
        setPendingPlayerCreate({ firstName, lastName, number, isSub });
        setIsLoading(false);
        return;
      }

      if (groups.length === 0) {
        const options = getSportPositionGroups(sport);
        addMessage(
          'assistant',
          `Got it — I can add **${firstName} ${lastName}**${isSub ? ' as a **sub**' : ''}. What position group(s) should I set?\nOptions: ${options.length ? options.join(', ') : 'none available for this sport'}.`
        );
        setPendingPlayerCreate({ firstName, lastName, number, isSub });
        setIsLoading(false);
        return;
      }

      try {
        const roleTypes = inferRoleTypesFromGroups(sport, groups);
        const { data: newPlayer, error: insertError } = await supabase
          .from('players')
          .insert({
            team_id: currentTeam.id,
            first_name: firstName,
            last_name: lastName,
            number: number ?? null,
            position: groups[0] || '--',
            experience: 'Beginner',
            injury_status: 'H',
            is_sub: isSub,
          })
          .select('id')
          .single();

        if (insertError || !newPlayer?.id) {
          console.error('Error creating player:', insertError);
          addMessage('assistant', "I couldn't create that player. Try again, or add them from the Roster page.");
          setIsLoading(false);
          return;
        }

        const card = await playerCardService.getOrCreatePlayerCard(newPlayer.id, currentTeam.id);
        await playerCardService.updatePlayerCard(card.id, {
          eligible_positions: groups,
          role_types: roleTypes,
        });
        await refreshTeamContextForAgent();

        addMessage(
          'assistant',
          `Done — I added **${firstName} ${lastName}** to the roster${isSub ? ' as a **sub**' : ''}.\nPosition groups: ${groups.join(', ')}.`,
          { label: `Open ${firstName} ${lastName}`, href: `/player-cards/${newPlayer.id}` }
        );
      } catch (err) {
        console.error(err);
        addMessage('assistant', "Something went wrong adding that player. Try again, or add them from the Roster page.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // General question: respond as Coach Steve agent (with live team/roster context when available)
    try {
      if (!user?.id) {
        addMessage('assistant', 'Please sign in to chat with Coach Steve.');
        setIsLoading(false);
        return;
      }
      await subscriptionService.ensureUsagePeriodResetted(user.id);
      const profile = await subscriptionService.getUserProfile(user.id);
      if (subscriptionService.hasReachedChatLimit(profile)) {
        addMessage(
          'assistant',
          `You've used your **${ROOKIE_LIMITS.CHAT_MESSAGES} AI Coach messages** this month. Upgrade to **Pro** for unlimited AI Coach, AI-powered practice & game plans, lineup optimization, and more — or your limit resets next month.`
        );
        setIsLoading(false);
        return;
      }
      const teamContext = currentTeam
        ? `Current team: ${currentTeam.name} (${currentTeam.sport}${currentTeam.ageLevel ? `, ${currentTeam.ageLevel}` : ''}).`
        : 'No team selected yet.';
      const rosterBlock = teamContextForAgent
        ? `\n\n${teamContextForAgent}\n\nUse only the LIVE TEAM DATA and GAME PERFORMANCE (if present) above to answer questions about lineups, positions, stats, and roster decisions. Reference specific players by name or number. When game performance data is present, use it to optimize lineup suggestions (e.g. high OBP at top, power in the middle) and pitching decisions (IP, K, ERA, WHIP, runs allowed). Both batting and pitching stats are included when recorded. Do not invent players or stats. The coach can apply suggestions in the app via Depth Chart or Roster.`
        : '';
      const systemPrompt = `You are Coach Steve, a helpful youth sports coaching assistant. Answer the user's question concisely and practically. ${teamContext}${rosterBlock} You can discuss practice ideas, drills, game strategy, and planning.

**Important — creation actions (use these exact phrases when directing the user):**
- **Practice plan:** say "create a practice plan" → I create a practice and can generate a plan.
- **Game plan** (Football only): say "create a game plan" → I open the game plan flow.
- **Game (schedule):** say "create a game" or "add a game" → I open the form to add a game to the schedule.
- **Formation:** Formations are created in the app under **Depth Chart** (set positions, then use the Formations button to save). I cannot create formations from chat. If they ask to create a formation, tell them to go to Depth Chart and use the Formations button to save their setup.
- **Assign/move player to formation:** say "put [player] at [position] in [formation]", "assign #5 to RB in Spread", or "move Ace to LDT in 4-3" (Football/Flag Football only) → I update the depth chart. Only the app can do this; never claim you moved or assigned a player unless the user used one of these exact phrasings and the app confirmed it.
- **Player:** say "add a player" or "add [First Last]" (with position groups) → I add them to the roster.
- **Injury status:** say "mark [player] out" or "set #5 as questionable" etc. → I update injury status.

Do not suggest "add a player" when they asked for a formation, game, game plan, or practice. If someone asks to move or assign a player to a position, tell them to use: **put [player] at [position] in [formation]** or **move [player] to [position] in [formation]** and I will do it. Use ** for bold. Keep replies focused and friendly.`;
      const conversationMessages = messages.slice(-10).map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));
      const apiMessages = [
        { role: 'system' as const, content: systemPrompt },
        ...conversationMessages,
        { role: 'user' as const, content: text },
      ];
      const reply = await generateAIContent(apiMessages, { timeout: 20000 });
      addMessage('assistant', reply.trim());
      await subscriptionService.incrementAiChatMessageCount(user.id);
    } catch (err) {
      console.error(err);
      addMessage(
        'assistant',
        "Something went wrong answering that. Try again, or ask me to **create a practice plan**, **create a game plan**, or **create a game** and I'll walk you through it."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const noTeam = !currentTeam;

  const panelHeader = isPanel && (
    <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-stone-200 bg-white">
      <div className="flex items-center gap-2 min-w-0">
        <MessageCircle className="h-5 w-5 flex-shrink-0 text-primary" />
        <span className="font-semibold text-dark truncate">Coach Steve</span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors flex-shrink-0"
        aria-label="Close panel"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );

  const content = (
    <>
      {!isPanel && (
        <PageHeader
          title="Coach Steve"
          icon={<MessageCircle className="h-6 w-6" />}
        />
      )}
      {noTeam ? (
        <div className={`rounded-2xl border border-stone-200 bg-white p-6 text-center ${isPanel ? 'm-4' : ''}`}>
          <p className="text-stone-600 mb-4">Select a team from the dashboard so Coach Steve can create practices and game plans for that team.</p>
          <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2">
            Go to dashboard
          </Link>
        </div>
      ) : (
        <>
          <div className={`rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden flex flex-col ${isPanel ? 'm-0 rounded-none border-0 border-b border-stone-200 flex-1 min-h-0' : ''}`}>
            {!isPanel && (
              <div className="p-4 border-b border-stone-100 bg-stone-50/50">
                <p className="text-sm text-stone-600">
                  Browsing as <strong className="text-dark">{currentTeam.name}</strong>
                </p>
              </div>
            )}
            {isPanel && (
              <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/50">
                <p className="text-sm text-stone-600">
                  Browsing as <strong className="text-dark">{currentTeam.name}</strong>
                </p>
              </div>
            )}

            <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isPanel ? 'min-h-0' : 'min-h-[320px] max-h-[50vh]'}`}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-stone-100 text-dark'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {msg.content.split('**').map((part, i) =>
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </p>
                    {msg.link && (
                      <Link
                        to={msg.link.href}
                        onClick={() => { if (isPanel && onClose) onClose(); }}
                        className="mt-2 inline-flex items-center gap-1 text-sm font-medium underline opacity-90 hover:opacity-100"
                      >
                        {msg.link.label} →
                      </Link>
                    )}
                  </div>
                </div>
              ))}

              {pendingAction === 'game_plan' && currentTeam?.sport === 'Football' && (
                <div className="flex justify-start">
                  <div className="max-w-full w-full rounded-2xl bg-stone-50 border border-stone-200 p-4">
                    <CoachSteveGamePlanForm
                      teamId={currentTeam.id}
                      teamName={currentTeam.name}
                      onSuccess={(result) => handleFormSuccess('game_plan', result)}
                      onCancel={handleFormCancel}
                    />
                  </div>
                </div>
              )}

              {pendingAction === 'create_game' && currentTeam && (
                <div className="flex justify-start">
                  <div className="max-w-full w-full">
                    <CoachSteveCreateGameForm
                      teamId={currentTeam.id}
                      initialValues={createGameInitialValues ?? undefined}
                      onSuccess={(game: Game) => {
                        setPendingAction(null);
                        setCreateGameInitialValues(null);
                        addMessage('assistant', `Done. I've added the game vs **${game.opponent_name}** (${game.date}).`, {
                          label: 'Open game',
                          href: `/games/${game.id}`,
                        });
                      }}
                      onCancel={() => {
                        setPendingAction(null);
                        setCreateGameInitialValues(null);
                      }}
                    />
                  </div>
                </div>
              )}

              {generationProgress && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl bg-primary bg-opacity-10 border border-primary border-opacity-20 px-4 py-2.5 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary" />
                    <p className="text-sm text-dark">
                      {generationProgress.split('**').map((part, i) =>
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </p>
                  </div>
                </div>
              )}

              {isLoading && !generationProgress && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-stone-100 px-4 py-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t border-stone-100 space-y-3">
              <div className="flex gap-2 flex-wrap">
                {currentTeam?.sport !== 'Football' && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!currentTeam) {
                        addMessage('assistant', "Please select a team first from the dashboard so I can add a game to their schedule.");
                        return;
                      }
                      addMessage('user', 'Create a game');
                      addMessage('assistant', "Sure — fill in the details below to add a game to the schedule.");
                      setCreateGameInitialValues(null);
                      setPendingAction('create_game');
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary bg-opacity-10 text-primary hover:bg-primary hover:text-white transition-colors"
                  >
                    <CalendarPlus className="h-4 w-4" />
                    Create game
                  </button>
                )}
                {currentTeam?.sport === 'Football' && (
                  <button
                    type="button"
                    onClick={() => handleQuickAction('game_plan')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary bg-opacity-10 text-primary hover:bg-primary hover:text-white transition-colors"
                  >
                    <Target className="h-4 w-4" />
                    Create game plan
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={pendingPracticeForFocus ? "e.g. tackling, red zone, or tempo..." : "Describe what you want (e.g. practice plan, game plan, or create a game)..."}
                  className="flex-1 min-h-[44px] max-h-32 rounded-xl border border-stone-200 px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={2}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  className="self-end p-2.5 rounded-xl bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );

  if (isPanel) {
    return (
      <div className="flex flex-col h-full min-h-0 bg-cream">
        {panelHeader}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {content}
        </div>
      </div>
    );
  }
  return <div className="max-w-3xl mx-auto">{content}</div>;
};

export default CoachSteve;
